import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, Loader2, MessagesSquare, Lock, ChevronRight, Users, Pin } from "lucide-react";
import { api } from "@/lib/api";
import { NewCircleDialog } from "@/components/chat/NewCircleDialog";
import { SearchButton, NotifBell, ActivityBell } from "@/components/common/HeaderActions";
import { ChatRowActions } from "@/components/chat/ChatRowActions";
import { timeAgo } from "@/lib/format";
import { cn } from "@/lib/utils";

function GroupAvatar({ chat, size = "md" }) {
  const dim = size === "md" ? "h-12 w-12" : "h-10 w-10";
  if (chat.icon_b64) {
    return <img src={chat.icon_b64} alt="" className={cn(dim, "shrink-0 rounded-2xl object-cover")} />;
  }
  return (
    <span className={cn(dim, "flex shrink-0 items-center justify-center rounded-2xl bg-bloq-mint/15 text-bloq-mint")}>
      <MessagesSquare className="h-5 w-5" />
    </span>
  );
}

function lastPreview(lm) {
  if (!lm) return "No messages yet";
  if (lm.e2ee) return "Encrypted message";
  if (lm.type === "system" || lm.sys_kind) return lm.text || "Updated the group";
  if (lm.kind === "deleted") return "Message deleted";
  return lm.text || (lm.image ? "Photo" : "New message");
}

export default function CirclesPage() {
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data: chats = [], isLoading } = useQuery({
    queryKey: ["circles-list"],
    queryFn: api.listChats,
    refetchInterval: 45000,
  });
  // Pinned Circles float to the top (v1.2.2), the rest keep server order.
  const circles = chats
    .filter((c) => c.type === "group")
    .sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0));

  const { data: limits } = useQuery({ queryKey: ["my-limits"], queryFn: api.myLimits });
  const cLimit = limits?.circles;
  const hasCap = cLimit && typeof cLimit.max === "number"; // max:null = admin/unlimited
  const atLimit = hasCap && cLimit.owned >= cLimit.max;

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-bloq-s1">
      <div className="flex items-center justify-between px-5 pb-2 pt-5">
        <div>
          <h1 className="text-2xl font-bold">Circles</h1>
          <div className="mt-0.5 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-bloq-mint">
            <Lock className="h-3 w-3" /> Small groups · up to 20
          </div>
        </div>
        <div className="flex items-center gap-2">
          <SearchButton />
          <ActivityBell />
          <NotifBell />
          <button
            onClick={() => (atLimit ? null : setDialogOpen(true))}
            disabled={atLimit}
            title={atLimit ? "You've reached your Circle limit — raise your Trust Score to create more" : "New Circle"}
            className="flex h-10 w-10 items-center justify-center rounded-2xl bloq-gradient text-bloq-bg transition-opacity hover:opacity-90 disabled:opacity-40"
            data-testid="new-circle-btn"
          >
            <Plus className="h-5 w-5" />
          </button>
        </div>
      </div>

      {cLimit && (
        <div className="mx-5 mb-2 flex items-center gap-2 rounded-xl border border-white/8 bg-bloq-s2 px-3 py-2 text-xs text-bloq-muted" data-testid="circle-quota">
          <Users className="h-3.5 w-3.5 text-bloq-mint" />
          {hasCap ? (
            <span><b className="text-white">{cLimit.owned}</b> of <b className="text-white">{cLimit.max}</b> Circles created</span>
          ) : (
            <span><b className="text-white">{cLimit.owned}</b> Circles · unlimited</span>
          )}
          {atLimit && <span className="text-bloq-purple">· raise your Trust Score for more</span>}
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-4">
        {isLoading ? (
          <div className="flex justify-center py-10 text-bloq-muted"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : circles.length === 0 ? (
          <div className="mx-2 mt-8 rounded-3xl border border-white/8 bg-bloq-s2 p-8 text-center" data-testid="circles-empty">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-bloq-mint/15 text-bloq-mint">
              <MessagesSquare className="h-7 w-7" />
            </span>
            <h2 className="mt-4 text-lg font-bold">No Circles yet</h2>
            <p className="mx-auto mt-1 max-w-xs text-sm text-bloq-muted">
              Create a small group (up to 20 people) to chat with your crew — private, encrypted, no strangers.
            </p>
            <button
              onClick={() => setDialogOpen(true)}
              disabled={atLimit}
              className="mt-5 inline-flex items-center gap-2 rounded-full bloq-gradient px-5 py-2.5 text-sm font-bold text-bloq-bg transition-opacity hover:opacity-90 disabled:opacity-40"
              data-testid="circles-empty-create-btn"
            >
              <Plus className="h-4 w-4" /> New Circle
            </button>
          </div>
        ) : (
          circles.map((c) => {
            const unread = c.unread > 0;
            return (
              <div
                key={c.id}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/app/chats/${c.id}`)}
                onKeyDown={(e) => { if (e.key === "Enter") navigate(`/app/chats/${c.id}`); }}
                className={cn(
                  "group flex w-full cursor-pointer items-center gap-3 rounded-2xl p-3 text-left transition-colors",
                  unread ? "bg-bloq-mint/[0.04] hover:bg-white/5" : "hover:bg-white/5",
                )}
                data-testid={`circle-row-${c.id}`}
              >
                <GroupAvatar chat={c} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className={cn("truncate font-semibold", unread ? "text-white" : "text-white/90")}>{c.name || "Circle"}</span>
                    {c.pinned && <Pin className="h-3 w-3 shrink-0 text-bloq-mint" data-testid={`circle-pinned-${c.id}`} />}
                    <span className="shrink-0 rounded-full bg-white/8 px-1.5 py-0.5 text-[10px] font-semibold text-bloq-muted">{c.member_count || (c.member_preview?.length ?? 0)}</span>
                  </div>
                  <div className={cn("mt-0.5 truncate text-sm", unread ? "font-medium text-white/85" : "text-bloq-muted")} data-testid={`circle-preview-${c.id}`}>
                    {lastPreview(c.last_message)}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <ChatRowActions chat={c} canLeave />
                  <div className="flex flex-col items-end gap-1">
                    <span className={cn("text-[11px]", unread ? "font-semibold text-bloq-mint" : "text-bloq-muted")}>{timeAgo(c.last_activity)}</span>
                    {unread ? (
                      <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-bloq-mint px-1.5 text-[11px] font-bold text-bloq-bg" data-testid={`circle-unread-${c.id}`}>{c.unread}</span>
                    ) : (
                      <ChevronRight className="h-4 w-4 text-bloq-muted" />
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <NewCircleDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </div>
  );
}
